import React, { useMemo } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { FiUser, FiUsers, FiMapPin, FiBriefcase, FiLoader } from 'react-icons/fi';

const campaignTypes = ['Mandatory', 'Remedial', 'Onboarding', 'Compliance'];

const MultiPill = ({ options, selected, onToggle, label, loading }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{label}</p>
    {loading ? (
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <SafeIcon icon={FiLoader} className="animate-spin" /> Loading...
      </div>
    ) : (
      <div className="flex flex-wrap gap-2">
        {options.map(o => (
          <button key={o} type="button" onClick={() => onToggle(o)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${selected.includes(o) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
            {o}
          </button>
        ))}
        {options.length === 0 && <span className="text-sm text-slate-400">No options available</span>}
      </div>
    )}
  </div>
);

const toggle = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

const StepAudience = ({ form, update, employees = [], loading, departmentsList = [], loadingDepartments }) => {
  const departments = useMemo(() => {
    if (departmentsList && departmentsList.length > 0) {
      return [...new Set(departmentsList.map(d => d.department_name).filter(Boolean))];
    }
    return [];
  }, [departmentsList]);

  const locations = useMemo(() => [...new Set(employees.map(l => l.location).filter(Boolean))], [employees]);

  // Use strictly string-based comparisons to avoid type mismatches when loading existing JSON
  const individualLearnersStrings = (form.individualLearners || []).map(String);
  
  const toggleLearner = (id) => {
    const stringId = String(id);
    const isSelected = individualLearnersStrings.includes(stringId);
    
    const newSelected = isSelected
      ? form.individualLearners.filter(x => String(x) !== stringId)
      : [...form.individualLearners, id]; // preserve original type when adding
      
    update({ individualLearners: newSelected });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Campaign Name <span className="text-red-500">*</span></label>
        <input value={form.campaignName} onChange={e => update({ campaignName: e.target.value })}
          placeholder="e.g. Q1 Security Awareness Training"
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-slate-50 focus:bg-white" />
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Campaign Type</label>
        <div className="flex flex-wrap gap-2">
          {campaignTypes.map(t => (
            <button key={t} type="button" onClick={() => update({ campaignType: t })}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.campaignType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <MultiPill 
        label="Filter by Department" 
        options={departments} 
        selected={form.departments}
        loading={loadingDepartments}
        onToggle={v => update({ departments: toggle(form.departments, v) })} 
      />

      <MultiPill 
        label="Filter by Location" 
        options={locations} 
        selected={form.locations}
        loading={loading}
        onToggle={v => update({ locations: toggle(form.locations, v) })} 
      />

      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Individual Learners</p>
        <div className="border border-slate-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-6 text-slate-500">
               <SafeIcon icon={FiLoader} className="animate-spin text-2xl mb-2" />
               <p className="text-sm">Loading learners...</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">No learners found.</div>
          ) : (
            employees.map(l => (
              <label key={l.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                <input type="checkbox" checked={individualLearnersStrings.includes(String(l.id))}
                  onChange={() => toggleLearner(l.id)} className="rounded accent-blue-600" />
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {(l.firstName?.[0] || '')}{(l.lastName?.[0] || '')}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">{l.firstName} {l.lastName}</p>
                  <p className="text-xs text-slate-400">{l.department || 'No dept'} · {l.location || 'Remote'}</p>
                </div>
              </label>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StepAudience;